import type { CallbackFlowOutcome, CallbackNotice } from '../models/callback.types'
import type { ShopifyOnboardingConnectionResult } from '../models/onboardingConnection.types'
import { CONNECTIONS_ROUTE } from '../routeTarget'

import type { AccountContextReloadOutcome } from '@/features/account'

const SUMMARY_ROUTE = { name: 'Summary' } as const

export interface OAuthAuthorizationCommand {
  success: '1' | '0'
  message?: string
  code?: string
}

export interface ShopifyOnboardingCommand {
  shopifySuccess: true
}

export type OAuthCallbackCommand = OAuthAuthorizationCommand | ShopifyOnboardingCommand

export interface OAuthCallbackFlowDependencies {
  reloadAccount: () => Promise<AccountContextReloadOutcome>
  hasShopifyIntegration: () => boolean
  notify: (notice: CallbackNotice) => void
  /**
   * The connect the user left mid-way for this consent screen (ORP-24744). A pending one sends a
   * successful return to Connections, where the page reopens the modal; a failed return drops it so
   * it cannot resurface on a later visit. Absent, every return goes to Summary as before.
   */
  connectIntent?: { pending: () => boolean; clear: () => void }
}

export class OAuthCallbackFlow {
  constructor(private readonly deps: OAuthCallbackFlowDependencies) {}

  execute(command: OAuthAuthorizationCommand): Promise<CallbackFlowOutcome>
  execute(command: ShopifyOnboardingCommand): Promise<ShopifyOnboardingConnectionResult>
  async execute(
    command: OAuthCallbackCommand,
  ): Promise<CallbackFlowOutcome | ShopifyOnboardingConnectionResult> {
    if ('success' in command && command.success === '0') {
      this.deps.connectIntent?.clear()
      if (command.code === 'OAUTH_ACCOUNT_MISMATCH') {
        this.deps.notify({ tone: 'warning', key: 'errors.api.OAUTH_ACCOUNT_MISMATCH' })
        return { destination: SUMMARY_ROUTE }
      }
      this.deps.notify({
        tone: 'warning',
        key: 'integrations.callback.authError',
        params: { detail: command.message ?? '' },
      })
      return { destination: SUMMARY_ROUTE }
    }

    if ('success' in command && command.success === '1') {
      await this.reload()
      this.deps.notify({ tone: 'success', key: 'integrations.callback.authSuccess' })
      return { destination: this.deps.connectIntent?.pending() ? CONNECTIONS_ROUTE : SUMMARY_ROUTE }
    }

    if ('shopifySuccess' in command) {
      // Capture before reload: after a first connection the refreshed payload legitimately contains
      // Shopify, so checking afterwards would misclassify the first connection as pre-existing.
      const existingIntegration = this.deps.hasShopifyIntegration()
      await this.reload()
      return { provider: 'shopify', existingIntegration }
    }

    throw new Error('Unsupported OAuth callback outcome')
  }

  private async reload(): Promise<void> {
    const result = await this.deps.reloadAccount()
    if (result.status === 'failed') throw result.error
  }
}
